import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDayOfWeek } from "./enums/enumDayOfWeek.generated";

// Model Objects

export class DeliveryArea extends ModelObjectWithDirtyCheck<DeliveryArea> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Lead Time in Days between Dispatch date and Delivery date.
    * Not Negative
    */
    public leadTimeDays: number = 0;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Array of days of week on which orders are typically despatched. Will always read in order Monday to Sunday.
    */
    public despatchDays: (EnumDayOfWeek)[] = [];
    public isHidden: boolean = false;
    private _deliveryServiceId: Guid | null = null;
    /**
    * Delivery Service, if any, to apply by default to orders for delivery in this delivery area. May be overriden by settings at outlet or group level.
    */
    public get deliveryServiceId(): Guid | null { return this._deliveryServiceId }
    public set deliveryServiceId(value: Guid | null) {
      this._deliveryServiceId = value === undefined ? null : value;
    }
    /**
    * Shows whether DispatchCutOffTime is for the same day or next day.If true, order placed before DispatchCutOffTime is dispatched on the same day else the next day.
    */
    public isSameDayDispatchCutOffTime: boolean = false;
    private _dispatchCutOffTime: LocalTime | null = null;
    /**
    * Cut off time for placing an order. Used to calculate dispatch date for this delivery area.
    */
    public get dispatchCutOffTime(): LocalTime | null { return this._dispatchCutOffTime }
    public set dispatchCutOffTime(value: LocalTime | null) {
      this._dispatchCutOffTime = value === undefined ? null : value;
    }
    /**
    * Allows for eg every week, every other week, every third week, etc.
    * Not Negative
    * Maximum : 12
    */
    public repeatWeeks: number = 0;
    private _repeatStartDate: LocalDate | null = null;
    /**
    * Date on which the weekly repeat starts. Should be set when RepeatWeeks > 1.
    */
    public get repeatStartDate(): LocalDate | null { return this._repeatStartDate }
    public set repeatStartDate(value: LocalDate | null) {
      this._repeatStartDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.leadTimeDays = jsonSerializedObject.leadTimeDays;
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.despatchDays = jsonSerializedObject.despatchDays.map((v: any) => EnumDayOfWeek.getByValue(v));
          this.isHidden = jsonSerializedObject.isHidden;
          this.deliveryServiceId = jsonSerializedObject.deliveryServiceId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryServiceId);
          this.isSameDayDispatchCutOffTime = jsonSerializedObject.isSameDayDispatchCutOffTime;
          this.dispatchCutOffTime = jsonSerializedObject.dispatchCutOffTime == null ? null : LocalTime.parse(jsonSerializedObject.dispatchCutOffTime);
          this.repeatWeeks = jsonSerializedObject.repeatWeeks;
          this.repeatStartDate = jsonSerializedObject.repeatStartDate == null ? null : LocalDate.parse(jsonSerializedObject.repeatStartDate);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.leadTimeDays = this.leadTimeDays;
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.despatchDays = this.despatchDays.map(v => v.value);
        toRet.isHidden = this.isHidden;
        toRet.deliveryServiceId = this.deliveryServiceId == null ? null : this.deliveryServiceId.toString();
        toRet.isSameDayDispatchCutOffTime = this.isSameDayDispatchCutOffTime;
        toRet.dispatchCutOffTime = this.dispatchCutOffTime == null ? null : this.dispatchCutOffTime.toString();
        toRet.repeatWeeks = this.repeatWeeks;
        toRet.repeatStartDate = this.repeatStartDate == null ? null : this.repeatStartDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "DeliveryArea.Id cannot be empty"},
            {field: "leadTimeDays", rule: (v: any) => v == null || v >= 0 || "LeadTimeDays is not greater than minimum allowed value: 0"},
            {field: "leadTimeDays", rule: (v: any) => v == null || v <= 2147483647 || "LeadTimeDays is above maximum allowed value: 2147483647"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "deliveryServiceId", rule: (v: any) => v == null || !v.isEmpty() || "DeliveryArea.DeliveryServiceId cannot be empty"},
            {field: "repeatWeeks", rule: (v: any) => v == null || v >= 0 || "RepeatWeeks is not greater than minimum allowed value: 0"},
            {field: "repeatWeeks", rule: (v: any) => v == null || v <= 12 || "RepeatWeeks is above maximum allowed value: 12"},
    ];
}


export class DeliveryAreaDistributionDates extends ModelObject<DeliveryAreaDistributionDates> {

    public id: Guid = Guid.createEmpty();
    public dispatchDate: LocalDate = LocalDate.ofEpochDay(0);
    public deliveryDate: LocalDate = LocalDate.ofEpochDay(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.dispatchDate = LocalDate.parse(jsonSerializedObject.dispatchDate);
          this.deliveryDate = LocalDate.parse(jsonSerializedObject.deliveryDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.dispatchDate = this.dispatchDate.toString();
        toRet.deliveryDate = this.deliveryDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "DeliveryAreaDistributionDates.Id cannot be empty"},
    ];
}


export class DeliveryAreaService extends ServiceBase {

  public async getAllDeliveryAreas(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DeliveryArea)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.deliveryAreas.map((v: any) => new DeliveryArea(v));;
    return this.makeJsonRequest("deliveryArea/v1/getAllDeliveryAreas", req, false, extractor, opts);
  };

  public async saveDeliveryAreas(deliveryAreas: readonly DeliveryArea[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    deliveryAreas:deliveryAreas.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("deliveryArea/v1/saveDeliveryAreas", req, true, extractor, opts);
  };

  public async setDeliveryAreaIsHidden(deliveryAreaId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    deliveryAreaId:deliveryAreaId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("deliveryArea/v1/setDeliveryAreaIsHidden", req, true, extractor, opts);
  };

  public async calculateDeliveryAreasDistributionDates(deliveryAreaIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DeliveryAreaDistributionDates)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    deliveryAreaIds:deliveryAreaIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.distributionDates.map((v: any) => new DeliveryAreaDistributionDates(v));;
    return this.makeJsonRequest("deliveryArea/v1/calculateDeliveryAreasDistributionDates", req, false, extractor, opts);
  };
}

export const deliveryAreaService = new DeliveryAreaService();